import React, { createContext, ReactElement, FC, useContext } from "react";
import { useEnsureAccountQuery } from "../data/repos/user";
import { CircularProgress, Box } from "@mui/material";

export type EnsureAccountStatus = "idle" | "checking" | "ready" | "polling" | "error";

type EnsureAccountContextState = {
	status: EnsureAccountStatus;
	isReady: boolean;
};

const initialState: EnsureAccountContextState = {
	status: "idle",
	isReady: false,
};

export const EnsureAccountContext = createContext<EnsureAccountContextState>(initialState);

interface IProps {
	children: ReactElement;
}

/**
 * EnsureAccountProvider
 *
 * Call ensure-account before other improve-api requests. When the user doesn't exist
 * in up_users yet, the backend enqueues creation and returns 202. This provider
 * polls get-me until the user is ready using React Query's refetchInterval.
 *
 * Only mount this provider when the user has an OAuth access token (see {@link App2AuthenticatedProviders} in Main).
 * - Calls ensure-account
 *   - 200: user exists, set ready, render children
 *   - 202: poll get-me until success, then set ready, render children
 */
export const EnsureAccountProvider: FC<IProps> = ({ children }) => {
	const { isReady, status } = useEnsureAccountQuery();

	if (!isReady) {
		return (
			<EnsureAccountContext.Provider value={{ status, isReady: false }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						height: "100vh",
						width: "100vw",
					}}
				>
					<CircularProgress />
				</Box>
			</EnsureAccountContext.Provider>
		);
	}

	return (
		<EnsureAccountContext.Provider value={{ status, isReady: true }}>
			{children}
		</EnsureAccountContext.Provider>
	);
};

export const useEnsureAccount = (): EnsureAccountContextState => {
	const context = useContext(EnsureAccountContext);
	if (context === undefined) {
		throw new Error("useEnsureAccount must be used within a EnsureAccountProvider");
	}
	return context;
};
